'use client';

import { useMedia } from 'react-use';

import { BREAKPOINTS } from '../constants';
import { useCacheKey } from './useCacheKey';
import { useDecisions } from './useDecisions';

type DeviceType = 'mobile' | 'tablet' | 'desktop';

type DeviceTypeResult = {
    deviceType: DeviceType;
    isDesktop: boolean;
    isTablet: boolean;
    isMobile: boolean;
};

/**
 * @deprecated Prefer CSS media queries for responsive UI. Use this hook only
 * for JavaScript behavior that CSS cannot express. The server uses the
 * User-Agent-based `x-petm-device` header, while the client uses media queries,
 * so the value may change after hydration and cause a layout shift.
 */
export const useDeviceType = (): DeviceTypeResult => {
    const { headers } = useCacheKey();
    const { breakpoint } = useDecisions();

    const deviceTypeFromHeader = headers.get('x-petm-device');

    const isTabletFromHeader = deviceTypeFromHeader === 'tablet';
    const isDesktopFromHeader = deviceTypeFromHeader === 'desktop';

    const isTablet = useMedia(`(min-width: ${BREAKPOINTS.sm2}px)`, isTabletFromHeader);
    const isDesktop = useMedia(`(min-width: ${BREAKPOINTS.lg}px)`, isDesktopFromHeader);

    const isTablet2 = useMedia(`(min-width: ${BREAKPOINTS.md}px)`, isTabletFromHeader);
    const isDesktop2 = useMedia(
        `(min-width: ${BREAKPOINTS.lg}px) and (hover: hover) and (pointer: fine)`,
        isDesktopFromHeader
    );

    const selectedDesktop = breakpoint ? isDesktop2 : isDesktop;
    const matchesTablet = breakpoint ? isTablet2 : isTablet;

    return {
        isDesktop: selectedDesktop,
        isTablet: !selectedDesktop && matchesTablet,
        isMobile: !matchesTablet && !selectedDesktop,
        deviceType: selectedDesktop ? 'desktop' : matchesTablet ? 'tablet' : 'mobile',
    };
};
